import React, { useState, useRef, useEffect } from 'react';
import { FiDownload, FiChevronDown } from 'react-icons/fi';
import { C } from './tokens';

export const exportCsv = (headers, rows, filename, title = '') => {
  const lines = [];
  if (title) lines.push(`"${title}"`);
  lines.push(headers.join(','));
  rows.forEach(row => {
    lines.push(row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
  });
  const csv = lines.join('\n');
  const a = Object.assign(document.createElement('a'), {
    href: URL.createObjectURL(new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })),
    download: `${filename}_${new Date().toISOString().slice(0, 10)}.csv`,
  });
  a.click(); URL.revokeObjectURL(a.href);
};

export const exportExcel = (headers, rows, filename, title = '') => {
  const esc = v => String(v ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const now = new Date().toLocaleString('en-IN');
  let html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"><title>${esc(title || filename)}</title></head><body>`;
  html += `<table border="1" style="border-collapse:collapse;font-family:Arial,sans-serif;font-size:11px;">`;
  
  // Title row
  if (title) {
    html += `<tr><td colspan="${headers.length}" style="background:#0D3B5E;color:#fff;font-size:14px;font-weight:bold;padding:8px;text-align:center;">${esc(title)}</td></tr>`;
  }
  
  // Report info row
  html += `<tr><td colspan="${headers.length}" style="background:#f3f4f6;color:#374151;font-size:10px;padding:6px;">Generated: ${now}</td></tr>`;
  
  // Header row
  html += `<thead><tr style="background:#0D3B5E;color:#fff;font-weight:bold;">`;
  headers.forEach(h => { html += `<th style="padding:8px;text-align:left;border:1px solid #ddd;">${esc(h)}</th>`; });
  html += `</tr></thead><tbody>`;
  
  // Data rows with alternating colors
  rows.forEach((r, i) => {
    const bg = i % 2 === 0 ? '#ffffff' : '#f9fafb';
    html += `<tr style="background:${bg};">`;
    r.forEach(c => { html += `<td style="padding:6px;border:1px solid #ddd;">${esc(c)}</td>`; });
    html += `</tr>`;
  });
  
  // Summary row
  html += `<tr style="background:#fef3c7;font-weight:bold;"><td colspan="${headers.length}" style="padding:8px;">Total: ${rows.length} records</td></tr>`;
  
  html += `</tbody></table></body></html>`;
  
  const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const a = Object.assign(document.createElement('a'), {
    href: URL.createObjectURL(blob),
    download: `${filename}_${new Date().toISOString().slice(0, 10)}.xls`,
  });
  a.click(); URL.revokeObjectURL(a.href);
};

/**
 * Split-button: primary click = CSV, dropdown arrow opens menu with CSV + Excel options.
 */
export function ExportButton({ onExport, loading = false, size = 'md', disabled = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const h  = size === 'sm' ? 32 : 36;
  const fs = size === 'sm' ? 12 : 13;
  const r  = size === 'sm' ? 7 : 8;
  const ic = size === 'sm' ? 11 : 12;

  const btnBase = {
    height: h, border: 'none', cursor: loading || disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit', fontSize: fs, fontWeight: 700,
    background: loading || disabled ? C.border : C.goldB,
    color: loading || disabled ? C.t4 : '#0D1B2A',
    display: 'flex', alignItems: 'center',
    opacity: loading || disabled ? 0.7 : 1,
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      {/* Primary button: CSV */}
      <button
        onClick={() => { if (!loading && !disabled) { onExport?.('csv'); } }}
        disabled={loading || disabled}
        style={{ ...btnBase, padding: size === 'sm' ? '0 12px' : '0 14px', borderRadius: `${r}px 0 0 ${r}px`, gap: 5, borderRight: `1px solid rgba(0,0,0,0.12)` }}>
        {loading
          ? <><div style={{ width:10,height:10,borderRadius:'50%',border:'2px solid rgba(13,27,42,0.3)',borderTopColor:'transparent',animation:'spin 0.8s linear infinite' }}/> Exporting…</>
          : <><FiDownload size={ic}/> Export</>}
      </button>

      {/* Dropdown arrow */}
      <button
        onClick={() => { if (!loading && !disabled) setOpen(o => !o); }}
        disabled={loading || disabled}
        style={{ ...btnBase, padding: '0 8px', borderRadius: `0 ${r}px ${r}px 0` }}>
        <FiChevronDown size={ic}/>
      </button>

      {/* Dropdown menu */}
      {open && (
        <div style={{ position: 'absolute', top: h + 4, right: 0, zIndex: 999,
          background: C.white, border: `1px solid ${C.border}`, borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: 150, overflow: 'hidden' }}>
          {[
            { label: 'Export as CSV',   format: 'csv',   ext: '.csv' },
            { label: 'Export as Excel', format: 'excel', ext: '.xls' },
          ].map(opt => (
            <button key={opt.format}
              onClick={() => { onExport?.(opt.format); setOpen(false); }}
              style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none',
                textAlign: 'left', fontSize: 13, fontWeight: 500, color: C.t1, cursor: 'pointer',
                fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 8 }}
              onMouseEnter={e => e.currentTarget.style.background = C.navyL}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              <FiDownload size={12} style={{ color: C.t3 }}/> {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
